import { Info, Users } from 'lucide-react';

import type { TableColumn } from '@/_shared/components/Table';
import { Table } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';

import { SlackTableSkeleton } from './SlackSkeletons';
import { type SlackUserLink, SUBSECTION_HEADING } from './types';

interface SlackConnectedUsersSectionProps {
  count: number;
  links: SlackUserLink[];
  columns: TableColumn<SlackUserLink>[];
  isLoading: boolean;
  isError: boolean;
}

function ConnectedUsersHeading({ count }: { count: number }): React.ReactElement {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <h3 className={SUBSECTION_HEADING}>
        Connected users
        {count > 0 && <span className="ml-1.5 text-muted-foreground/50">{count}</span>}
      </h3>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="text"
            iconOnly
            Icon={<Info className="size-3.5 shrink-0" />}
            aria-label="About connected users"
            className="cursor-help text-muted-foreground/70 hover:text-muted-foreground"
          />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Slack users who connected a Lookout workspace with <code>/lookout connect</code>. Members who DM the bot as
          guests without connecting a workspace are not listed here.
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function SlackConnectedUsersSection({
  count,
  links,
  columns,
  isLoading,
  isError,
}: SlackConnectedUsersSectionProps): React.ReactElement {
  return (
    <div>
      <ConnectedUsersHeading count={count} />
      {isLoading ? (
        <SlackTableSkeleton />
      ) : isError ? (
        <Callout variant="error">Failed to load connected users.</Callout>
      ) : links.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Users />
            </EmptyMedia>
            <EmptyTitle>No connected users yet</EmptyTitle>
            <EmptyDescription>
              People connect a Lookout workspace by running <code>/lookout connect</code> in Slack. Guest DMs without
              connecting a workspace are not shown here.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table
          data={links}
          columns={columns}
          getRowId={(link) => `${link.teamId}-${link.slackUserId}`}
          fixedLayout
          maxHeight="24rem"
        />
      )}
    </div>
  );
}
